import { z } from 'zod';
import { LootEntrySchema, totalChance } from '../rules/loot.js';
import { BOSS_DEFINITIONS, BOSS_MOB_IDS } from './boss-mobs.js';
import { BOUNTIES, BOUNTY_MOB_IDS, WANTED_BOARD_MOB_ID } from './bounties.js';
import { COMPANY_MOB_IDS, COMPANY_RECRUITS, companyMobOf } from './company.js';
import { EVENT_BIOMES, EVENT_MOB_IDS, giantOf, GOLD_CART_ID, invaderOf, warlordOf, } from './event-mobs.js';
import { RARITIES } from './items.js';
import { LookSchema } from './look.js';
import { NIGHT_MOB_IDS, NIGHT_MOBS } from './night-mobs.js';
import { QUARRY_KIN, QUARRY_MOB_IDS } from './quarry-mobs.js';
import { RARE_MOB_IDS, RARE_MOBS } from './rare-mobs.js';
import { QUEST_NPC_IDS, QUEST_NPCS, QUEST_XP } from './quests/regions.js';
import { ARENA_ALLY_HEALTH, ARENA_ALLY_IDS, ARENA_ALLY_SOURCES, SHEFIRA_DEFINITIONS, SHEFIRA_MOB_IDS, } from './shefira-mobs.js';
/**
 * Monsters. Data, not code (CLAUDE.md rule 4): the server validates this table
 * at boot and the AI, combat and respawn rules read from it.
 *
 * Three or four creatures per biome (data/biomes.ts), stronger the further
 * the biome lies from the capital: the meadow's rat, fox and boar for
 * newcomers, then the forest and the swamp, then the desert and the snow.
 * Within a biome they fight differently (quick and frail, slow and heavy,
 * a pack, an ambusher), so a band of levels is not one fight repeated. A level-1 Warrior
 * hits for 12 and has 120 health, about 30 and 348 by level 20 before gear.
 * Creature defence makes a stronger biome demand its predecessor's equipment.
 *
 * Their tables are lean on purpose: a kill leaves one thing at most, often
 * nothing, a
 * material a quarter of the time, a potion now and then, and one kill in
 * twenty the smith's common material of their biome (ADR 0013). Equipment is the
 * biome's (`data/loot.ts`), about one piece every thirty-odd kills.
 */
/**
 * The elites (ADR 0023): one rare, one epic and one legendary per biome
 * (`elites` in `data/biomes.ts`), each a stronger kin of one of its
 * creatures, drawn with that creature's body and named in the colour of its
 * rarity. Few and slow to come back, so meeting one stays an event.
 */
export const ELITE_MOB_IDS = [
    'plague-rat',
    'scarred-boar',
    'iron-snout',
    'ash-wolf',
    'goblin-chief',
    'silver-fang',
    'venom-toad',
    'lizard-shaman',
    'bog-king',
    'mangy-hyena',
    'cursed-mummy',
    'forgotten-pharaoh',
    'ice-bear',
    'howling-yeti',
    'elder-blizzard',
];
export const MOB_IDS = [
    'rat',
    'fox',
    'boar',
    'wolf',
    'goblin',
    'treant',
    'goblin-archer',
    'toad',
    'wisp',
    'lizardman',
    'caiman',
    'hyena',
    'scorpion',
    'cobra',
    'mummy',
    'ember-salamander',
    'ice-wolf',
    'polar-bear',
    'snow-harpy',
    'frost-revenant',
    'yeti',
    ...ELITE_MOB_IDS,
    ...BOSS_MOB_IDS,
    // The world events' raiders and warlords (ADR 0050, `event-mobs.ts`).
    ...EVENT_MOB_IDS,
    // The creatures a dragon's mark changed (ADR 0087, `quarry-mobs.ts`).
    ...QUARRY_MOB_IDS,
    // The wandering rares (ADR 0094, `rare-mobs.ts`).
    ...RARE_MOB_IDS,
    // The creatures of the night (ADR 0093, `night-mobs.ts`).
    ...NIGHT_MOB_IDS,
    // The targets of the wanted boards (ADR 0096, `bounties.ts`).
    ...BOUNTY_MOB_IDS,
    'villager',
    'farmer',
    'guard',
    'smith',
    'runesmith',
    'merchant',
    'smelter',
    'alchemist',
    'auction-sign',
    'stash-chest',
    WANTED_BOARD_MOB_ID,
    'tutor',
    'captain',
    'hermit',
    'lookout',
    'ranger',
    'deer',
    'horse',
    'stablemaster',
    'camel',
    'camel-driver',
    'company-sentry',
    // The road encounters' peddler and waylaid traveller (ADR 0093, `encounters.ts`).
    'peddler',
    'wayfarer',
    // The townsfolk the regions' quests add (ADR 0047, `quests/<biome>.ts`).
    ...QUEST_NPC_IDS,
    // The recruits at the Company's quarter (ADR 0067, `company.ts`).
    ...COMPANY_MOB_IDS,
    // Shefira, her brood and the Company's arena copies (ADR 0068, `shefira-mobs.ts`).
    ...SHEFIRA_MOB_IDS,
];
export const MobIdSchema = z.enum(MOB_IDS);
/**
 * What a mob is to the loot (`data/loot.ts`): an ordinary creature, the rare
 * boss of an open-world place, or the boss at the end of a dungeon.
 */
export const MOB_RANKS = ['ordinary', 'zone-boss', 'dungeon-boss'];
/**
 * Pictograms the minimap draws for a townsperson worth finding (the smith's
 * anvil, the merchant's coins, the smelter's crucible, the alchemist's
 * flask, the auction house's scales, a quest giver's scroll), so a village
 * reads at a glance.
 */
export const MAP_ICONS = [
    'anvil',
    'coins',
    'crucible',
    'flask',
    'scales',
    'quest',
    'horseshoe',
];
/**
 * How a monster's shot looks in flight (ADR 0055): the client draws each
 * one in palette pixels, as it draws the spells' effects.
 */
export const PROJECTILE_KINDS = ['arrow', 'wisp', 'venom', 'ice-shard'];
/**
 * A monster that shoots (ADR 0055): it stops as soon as its prey stands in
 * range and in plain sight, and shoots a homing projectile that deals its
 * `damage` on landing. It never backs away: a player who walks up to it
 * meets its weaker `meleeDamage` instead.
 */
export const RangedAttackSchema = z
    .object({
    /** Farthest ring it shoots from; within `aggroRange`, or it would notice nobody out there. */
    range: z.number().int().min(2).max(8),
    projectile: z.enum(PROJECTILE_KINDS),
    /** Flight speed: the shot lands this many tiles per second after it leaves. */
    tilesPerSecond: z.number().positive().max(30),
    /** Its blow on a neighbour, before the target's defence, like `damage`. */
    meleeDamage: z.number().int().min(0),
})
    .strict();
/**
 * How a creature fights beyond the plain chase (`rules/mob-ai.ts`): it walks
 * a circuit round home instead of standing (`patrol`), backs away a moment
 * when nearly dead (`flee`), or brings its kin near by into the fight when
 * struck (`call-for-help`). Ordinary creatures only: the server ignores them
 * on elites, bosses, camps, guardians and anything summoned (`MobController`).
 */
export const MOB_BEHAVIOURS = ['patrol', 'flee', 'call-for-help'];
export const MobDefinitionSchema = z.object({
    id: MobIdSchema,
    /** Display name, French like all player-facing text. */
    name: z.string().min(1),
    /** Logical body sprite key in the asset manifest. */
    bodyKey: z.string().min(1),
    /** Walking speed in tiles per second; the server paces the mob by it. */
    tilesPerSecond: z.number().positive().max(20),
    health: z.number().int().positive(),
    /** Damage of a blow before the target's defence (`rules/combat.ts`, `mobDamage`). */
    damage: z.number().int().min(0),
    /** Flat protection against each player hit, including spells. */
    defence: z.number().int().min(0).default(0),
    attackIntervalMs: z.number().int().positive(),
    /** Shoots from afar rather than biting (ADR 0055); absent for a melee creature. */
    ranged: RangedAttackSchema.optional(),
    /** A player this close (in tiles, Manhattan) is attacked. */
    aggroRange: z.number().int().min(1),
    /** This far from its spawn point the mob gives up and walks home. */
    leashRange: z.number().int().min(1),
    /** Delay between death and reappearance at the spawn point. */
    respawnMs: z.number().int().positive(),
    xp: z.number().int().min(0),
    /**
     * Shown before its name, so a player reads the danger before the fight. Set
     * by hand along the tier curve, beside the health and the damage it sums up;
     * absent on the peaceful, who are not measured against anyone.
     */
    // Not capped at the player's MAX_LEVEL: elites and bosses stand above it.
    level: z.number().int().min(1).max(30).optional(),
    /**
     * Materials and consumables it may leave on the ground: one of them at
     * most, a single roll sharing out their chances (`rollOneOf`). Equipment is not listed here: it comes from its biome's pool, under
     * the odds of its rank (`data/loot.ts`).
     */
    loot: z
        .array(LootEntrySchema)
        .refine((table) => totalChance(table) <= 1, 'one roll shares [0, 1): chances sum to 1 at most')
        .default([]),
    rank: z.enum(MOB_RANKS).default('ordinary'),
    /**
     * Common for every ordinary creature; rare, epic or legendary for an elite
     * (ADR 0023): the colour of its name, its loot (`ELITE_LOOT`), and how long
     * it keeps away (`ELITE_SCALING`). The same scale as the equipment's.
     */
    rarity: z.enum(RARITIES).default('common'),
    /**
     * Villagers and wild deer: never aggro, cannot be attacked, give nothing.
     * They are the valley's life, not its danger.
     */
    peaceful: z.boolean().default(false),
    /**
     * A calm kind: it strolls beside a player without a glance and fights only
     * once struck, and only the one who struck it. The player picks their
     * fights in a crowded den. Its elites, a camp's keepers and a place's
     * guardians stay on the hunt (the server decides, `MobController`).
     */
    passive: z.boolean().default(false),
    /** Its ways in a fight (`MOB_BEHAVIOURS`); none, the plain chase, by default. */
    behaviours: z.array(z.enum(MOB_BEHAVIOURS)).default([]),
    /**
     * A townsperson who answers a click (the smith, the merchant): named in
     * green, the hand cursor over them, and a click walks up to them and opens
     * their window. Any NPC with something to say sets it; the client reads it.
     */
    interactive: z.boolean().default(false),
    /** Their pictogram on the minimap and the world map, if they are worth finding. */
    mapIcon: z.enum(MAP_ICONS).optional(),
    /** Whether its name floats over it; a deer is just a deer. */
    nameplate: z.boolean().default(true),
    /** Tiles from home it strolls to when idle; 0 stands still. */
    wanderRadius: z.number().int().min(0).max(12).default(3),
    /** Average pause between two strolls, so townsfolk linger instead of pacing. */
    restMs: z.number().int().positive().default(3000),
    /**
     * Drawn this many times its body's size, whole steps for the pixels' sake:
     * the world event's giant (ADR 0050). Display only; it still holds one tile.
     */
    scale: z.number().int().min(1).max(3).default(1),
    /**
     * Its melee blow lands on every player within this many tiles of it, not
     * on its prey alone: the world event's giant (SHE-160), so one fighter
     * drinking potions cannot hold it for the others. Shots hit their target only.
     */
    cleave: z.number().int().min(1).max(3).optional(),
    /**
     * A townsperson drawn as a player (the tutor, ADR 0030): the class body
     * under these pieces, by template id, all at this smith's level, with this
     * hair and these eyes, on his mount if he has one. Game data the client reads, never synchronised; a
     * test checks every piece against the items.
     */
    outfit: z
        .object({
        helmet: z.string().min(1),
        chest: z.string().min(1),
        weapon: z.string().min(1),
        offhand: z.string().min(1),
        gloves: z.string().min(1),
        boots: z.string().min(1),
        level: z.number().int().min(0).max(10),
        look: LookSchema,
        /** The companion id of the mount he sits on, and the name he gives it. */
        mount: z.string().min(1).optional(),
        mountName: z.string().min(1).optional(),
    })
        .optional(),
});
const DEFINITIONS = {
    rat: {
        id: 'rat',
        name: 'Rat',
        bodyKey: 'body.rat',
        // Slower than every class: a player can always walk away from a rat.
        tilesPerSecond: 3,
        health: 48,
        damage: 7,
        attackIntervalMs: 1200,
        aggroRange: 4,
        behaviours: ['flee'],
        leashRange: 10,
        respawnMs: 10_000,
        // A fifth under what it paid before the meadow's quests (ADR 0047).
        xp: 4,
        level: 1,
        loot: [
            { templateId: 'flint', chance: 0.05 },
            { templateId: 'rat-tail', chance: 0.45 },
            { templateId: 'rat-pelt', chance: 0.1 },
            { templateId: 'bread', chance: 0.05 },
            { templateId: 'potion-small', chance: 0.03 },
        ],
    },
    fox: {
        id: 'fox',
        name: 'Renard',
        bodyKey: 'body.fox',
        // Quick, frail and never still: it bites often and roams wide, a lesson
        // in keeping up the pressure before the boar teaches patience.
        tilesPerSecond: 4,
        health: 60,
        damage: 9,
        defence: 1,
        attackIntervalMs: 1000,
        aggroRange: 3,
        behaviours: ['patrol'],
        passive: true,
        leashRange: 8,
        respawnMs: 15_000,
        xp: 9,
        level: 2,
        wanderRadius: 5,
        loot: [
            { templateId: 'flint', chance: 0.05 },
            { templateId: 'fox-tail', chance: 0.45 },
            { templateId: 'bread', chance: 0.05 },
            { templateId: 'potion-small', chance: 0.03 },
        ],
    },
    boar: {
        id: 'boar',
        name: 'Sanglier',
        bodyKey: 'body.boar',
        // As fast as a walking Warrior: running from a boar only works with a head start.
        tilesPerSecond: 4,
        health: 95,
        damage: 13,
        defence: 2,
        attackIntervalMs: 1500,
        // Territorial rather than hunting: it charges who comes close, not who passes by.
        aggroRange: 3,
        passive: true,
        leashRange: 8,
        respawnMs: 20_000,
        xp: 14,
        level: 3,
        loot: [
            { templateId: 'flint', chance: 0.05 },
            { templateId: 'boar-tusk', chance: 0.4 },
            { templateId: 'boar-hide', chance: 0.12 },
            { templateId: 'bread', chance: 0.06 },
            { templateId: 'potion-small', chance: 0.04 },
        ],
    },
    wolf: {
        id: 'wolf',
        name: 'Loup',
        bodyKey: 'body.wolf',
        // A pack hunter: quick, keen-eyed, and never alone.
        tilesPerSecond: 4.5,
        health: 135,
        damage: 16,
        defence: 6,
        attackIntervalMs: 1300,
        aggroRange: 5,
        behaviours: ['call-for-help'],
        leashRange: 10,
        respawnMs: 20_000,
        xp: 26,
        level: 5,
        loot: [
            { templateId: 'amber-resin', chance: 0.05 },
            { templateId: 'wolf-pelt', chance: 0.4 },
            { templateId: 'bread', chance: 0.05 },
            { templateId: 'potion-small', chance: 0.05 },
        ],
    },
    goblin: {
        id: 'goblin',
        name: 'Gobelin',
        bodyKey: 'body.goblin',
        tilesPerSecond: 3.5,
        health: 160,
        damage: 18,
        defence: 7,
        attackIntervalMs: 1400,
        aggroRange: 4,
        behaviours: ['patrol'],
        leashRange: 9,
        respawnMs: 20_000,
        xp: 30,
        level: 6,
        loot: [
            { templateId: 'amber-resin', chance: 0.05 },
            { templateId: 'goblin-ear', chance: 0.4 },
            { templateId: 'bread', chance: 0.06 },
            { templateId: 'potion-small', chance: 0.05 },
        ],
    },
    treant: {
        id: 'treant',
        name: 'Sylvain',
        bodyKey: 'body.treant',
        // Rooted and slow: easy to walk away from, long to fell, and each blow
        // lands hard. The forest's wall after its quick wolves.
        tilesPerSecond: 2.5,
        health: 220,
        damage: 23,
        defence: 10,
        attackIntervalMs: 2000,
        aggroRange: 3,
        passive: true,
        leashRange: 7,
        respawnMs: 25_000,
        xp: 35,
        level: 7,
        wanderRadius: 1,
        restMs: 6000,
        loot: [
            { templateId: 'amber-resin', chance: 0.05 },
            { templateId: 'treant-bark', chance: 0.4 },
            { templateId: 'potion-small', chance: 0.05 },
        ],
    },
    'goblin-archer': {
        id: 'goblin-archer',
        name: 'Archer gobelin',
        bodyKey: 'body.goblin-archer',
        // The first shooter (ADR 0055): frail, it stands off and looses arrows;
        // walk up to it and its dagger is a poor thing next to its bow.
        tilesPerSecond: 3.5,
        health: 120,
        damage: 16,
        defence: 5,
        attackIntervalMs: 1800,
        ranged: { range: 5, projectile: 'arrow', tilesPerSecond: 10, meleeDamage: 8 },
        aggroRange: 6,
        leashRange: 10,
        respawnMs: 20_000,
        xp: 30,
        level: 6,
        loot: [
            { templateId: 'amber-resin', chance: 0.05 },
            { templateId: 'goblin-fletching', chance: 0.4 },
            { templateId: 'bread', chance: 0.05 },
            { templateId: 'potion-small', chance: 0.05 },
        ],
    },
    toad: {
        id: 'toad',
        name: 'Crapaud géant',
        bodyKey: 'body.toad',
        // Slow and patient: it waits by the bog for whoever wanders too close.
        tilesPerSecond: 3,
        health: 210,
        damage: 35,
        defence: 14,
        attackIntervalMs: 1600,
        aggroRange: 3,
        passive: true,
        leashRange: 8,
        respawnMs: 25_000,
        xp: 40,
        level: 8,
        loot: [
            { templateId: 'bog-clay', chance: 0.05 },
            { templateId: 'toad-slime', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    wisp: {
        id: 'wisp',
        name: 'Feu follet',
        bodyKey: 'body.wisp',
        // It drifts over the bog and throws its pale flame from afar: a slow
        // orb, but it never misses, and the wisp keeps its distance.
        tilesPerSecond: 3,
        health: 190,
        damage: 30,
        defence: 12,
        attackIntervalMs: 1900,
        ranged: { range: 5, projectile: 'wisp', tilesPerSecond: 7, meleeDamage: 12 },
        aggroRange: 6,
        behaviours: ['patrol'],
        leashRange: 10,
        respawnMs: 25_000,
        xp: 45,
        level: 9,
        loot: [
            { templateId: 'bog-clay', chance: 0.05 },
            { templateId: 'wisp-glow', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    lizardman: {
        id: 'lizardman',
        name: 'Homme-lézard',
        bodyKey: 'body.lizardman',
        tilesPerSecond: 3.5,
        health: 240,
        damage: 40,
        defence: 16,
        attackIntervalMs: 1500,
        aggroRange: 4,
        behaviours: ['call-for-help'],
        leashRange: 9,
        respawnMs: 25_000,
        xp: 50,
        level: 10,
        loot: [
            { templateId: 'bog-clay', chance: 0.05 },
            { templateId: 'lizard-scale', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    caiman: {
        id: 'caiman',
        name: 'Caïman',
        bodyKey: 'body.caiman',
        // An ambusher: it lies still and notices only who steps beside it, then
        // bites for more than anything else in the swamp, slowly.
        tilesPerSecond: 3,
        health: 290,
        damage: 48,
        defence: 18,
        attackIntervalMs: 2000,
        aggroRange: 2,
        leashRange: 7,
        respawnMs: 25_000,
        xp: 55,
        level: 11,
        wanderRadius: 1,
        restMs: 8000,
        loot: [
            { templateId: 'bog-clay', chance: 0.05 },
            { templateId: 'caiman-tooth', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    hyena: {
        id: 'hyena',
        name: 'Hyène',
        bodyKey: 'body.hyena',
        tilesPerSecond: 4.5,
        health: 280,
        damage: 30,
        defence: 20,
        attackIntervalMs: 1300,
        aggroRange: 5,
        behaviours: ['call-for-help', 'flee'],
        leashRange: 10,
        respawnMs: 25_000,
        xp: 60,
        level: 12,
        loot: [
            { templateId: 'glass-sand', chance: 0.05 },
            { templateId: 'hyena-fang', chance: 0.25 },
            { templateId: 'bread', chance: 0.06 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    scorpion: {
        id: 'scorpion',
        name: 'Scorpion géant',
        bodyKey: 'body.scorpion',
        // Armoured and quick to sting: light blows, but many, on a shell that
        // takes a while to crack.
        tilesPerSecond: 3.5,
        health: 320,
        damage: 23,
        defence: 24,
        attackIntervalMs: 900,
        aggroRange: 4,
        behaviours: ['patrol'],
        passive: true,
        leashRange: 9,
        respawnMs: 25_000,
        xp: 68,
        level: 13,
        wanderRadius: 2,
        loot: [
            { templateId: 'glass-sand', chance: 0.05 },
            { templateId: 'scorpion-stinger', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    cobra: {
        id: 'cobra',
        name: 'Cobra cracheur',
        bodyKey: 'body.cobra',
        // Spits its venom from four tiles, quick and often, then bites weakly
        // when cornered: the desert's answer to a player who waits for it.
        tilesPerSecond: 3.5,
        health: 250,
        damage: 34,
        defence: 20,
        attackIntervalMs: 1700,
        ranged: { range: 4, projectile: 'venom', tilesPerSecond: 9, meleeDamage: 16 },
        aggroRange: 6,
        leashRange: 10,
        respawnMs: 25_000,
        xp: 66,
        level: 13,
        loot: [
            { templateId: 'glass-sand', chance: 0.05 },
            { templateId: 'cobra-venom', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.06 },
        ],
    },
    mummy: {
        id: 'mummy',
        name: 'Momie',
        bodyKey: 'body.mummy',
        // The slowest thing in the valley, and the hardest to put down.
        tilesPerSecond: 2.5,
        health: 350,
        damage: 35,
        defence: 26,
        attackIntervalMs: 1800,
        aggroRange: 4,
        leashRange: 8,
        respawnMs: 30_000,
        xp: 75,
        level: 14,
        loot: [
            { templateId: 'glass-sand', chance: 0.05 },
            { templateId: 'mummy-wrap', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.07 },
        ],
    },
    'ember-salamander': {
        id: 'ember-salamander',
        name: 'Salamandre de braise',
        bodyKey: 'body.ember-salamander',
        // The quickest thing in the valley and keen-eyed: it spots a player from
        // afar and cannot be outrun, but it burns out fast.
        tilesPerSecond: 5,
        health: 290,
        damage: 36,
        defence: 22,
        attackIntervalMs: 1500,
        aggroRange: 6,
        leashRange: 11,
        respawnMs: 30_000,
        xp: 82,
        level: 15,
        wanderRadius: 4,
        loot: [
            { templateId: 'glass-sand', chance: 0.05 },
            { templateId: 'ember-scale', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.07 },
        ],
    },
    'ice-wolf': {
        id: 'ice-wolf',
        name: 'Loup des glaces',
        bodyKey: 'body.ice-wolf',
        // The snow's pack: big packs, keen noses, quick bites; one is easy,
        // five at once is the lesson.
        tilesPerSecond: 4.5,
        health: 320,
        damage: 30,
        defence: 28,
        attackIntervalMs: 1100,
        aggroRange: 6,
        behaviours: ['call-for-help'],
        leashRange: 11,
        respawnMs: 30_000,
        xp: 86,
        level: 16,
        wanderRadius: 4,
        loot: [
            { templateId: 'frost-shard', chance: 0.05 },
            { templateId: 'ice-wolf-fang', chance: 0.25 },
            { templateId: 'bread', chance: 0.06 },
            { templateId: 'potion-small', chance: 0.07 },
        ],
    },
    'polar-bear': {
        id: 'polar-bear',
        name: 'Ours blanc',
        bodyKey: 'body.polar-bear',
        tilesPerSecond: 3.5,
        health: 360,
        damage: 35,
        defence: 30,
        attackIntervalMs: 1700,
        aggroRange: 4,
        passive: true,
        leashRange: 9,
        respawnMs: 30_000,
        xp: 90,
        level: 17,
        loot: [
            { templateId: 'frost-shard', chance: 0.05 },
            { templateId: 'bear-fur', chance: 0.25 },
            { templateId: 'bread', chance: 0.07 },
            { templateId: 'potion-small', chance: 0.07 },
        ],
    },
    'snow-harpy': {
        id: 'snow-harpy',
        name: 'Harpie des neiges',
        bodyKey: 'body.snow-harpy',
        // Sharp-eyed: it sees far, shoots quick shards of ice, and its talons
        // are no match for a warrior who reaches it.
        tilesPerSecond: 4,
        health: 330,
        damage: 44,
        defence: 30,
        attackIntervalMs: 1900,
        ranged: { range: 5, projectile: 'ice-shard', tilesPerSecond: 12, meleeDamage: 20 },
        aggroRange: 7,
        leashRange: 12,
        respawnMs: 30_000,
        xp: 96,
        level: 18,
        loot: [
            { templateId: 'frost-shard', chance: 0.05 },
            { templateId: 'harpy-feather', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.07 },
        ],
    },
    'frost-revenant': {
        id: 'frost-revenant',
        name: 'Revenant du givre',
        bodyKey: 'body.frost-revenant',
        // A dead knight on patrol: slow, heavy sword blows, the hardest hitter
        // below the yeti.
        tilesPerSecond: 3,
        health: 400,
        damage: 45,
        defence: 34,
        attackIntervalMs: 2100,
        aggroRange: 5,
        behaviours: ['patrol'],
        leashRange: 10,
        respawnMs: 35_000,
        xp: 102,
        level: 19,
        wanderRadius: 2,
        loot: [
            { templateId: 'frost-shard', chance: 0.05 },
            { templateId: 'frozen-heart', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.08 },
        ],
    },
    yeti: {
        id: 'yeti',
        name: 'Yéti',
        bodyKey: 'body.yeti',
        tilesPerSecond: 3.5,
        health: 450,
        damage: 42,
        defence: 36,
        attackIntervalMs: 1800,
        aggroRange: 4,
        leashRange: 9,
        respawnMs: 35_000,
        xp: 110,
        level: 20,
        loot: [
            { templateId: 'frost-shard', chance: 0.05 },
            { templateId: 'yeti-horn', chance: 0.25 },
            { templateId: 'potion-small', chance: 0.08 },
        ],
    },
    villager: {
        id: 'villager',
        name: 'Villageoise',
        bodyKey: 'body.villager',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Lingers at the market: a few steps, then a long chat.
        wanderRadius: 3,
        restMs: 9000,
    },
    farmer: {
        id: 'farmer',
        name: 'Fermier',
        bodyKey: 'body.farmer',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Works its own field (the den sits on it), row by row.
        wanderRadius: 2,
        restMs: 6000,
    },
    guard: {
        id: 'guard',
        name: 'Garde',
        bodyKey: 'body.guard',
        tilesPerSecond: 2.5,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Stands its post at the gate.
        wanderRadius: 0,
    },
    // The Company's sentries (ADR 0067): the guard's body in the Company's
    // white (scripts/recolour-company-sentry.py), standing at its bastion's gate.
    'company-sentry': {
        id: 'company-sentry',
        name: 'Sentinelle',
        bodyKey: 'body.company-sentry',
        tilesPerSecond: 2.5,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
    },
    smith: {
        id: 'smith',
        name: 'Forgeron',
        bodyKey: 'body.smith',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Never leaves his anvil: players come to him (ADR 0013).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'anvil',
    },
    runesmith: {
        id: 'runesmith',
        name: 'Graveur de runes',
        bodyKey: 'body.runesmith',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Beside the forge of Sablerouge and Givrecime, rerolling affixes (ADR 0057).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'anvil',
    },
    merchant: {
        id: 'merchant',
        name: 'Marchand',
        bodyKey: 'body.merchant',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps his shop: whatever you found, he buys it there (ADR 0016).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'coins',
    },
    // The road encounters (ADR 0093): a pedlar who stops a few minutes on a
    // road with one rare lot, and a traveller waylaid by raiders. The merchant's
    // and the villager's bodies until their own are drawn.
    peddler: {
        id: 'peddler',
        name: 'Colporteur',
        bodyKey: 'body.merchant',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
        interactive: true,
    },
    wayfarer: {
        id: 'wayfarer',
        name: 'Voyageuse',
        bodyKey: 'body.villager',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
    },
    smelter: {
        id: 'smelter',
        name: 'Fondeur',
        bodyKey: 'body.smelter',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps his foundry: five pieces in, one better out (ADR 0018).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'crucible',
    },
    tutor: {
        id: 'tutor',
        name: 'Ser Givrecoeur',
        bodyKey: 'body.warrior',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Waits by the camp's spawn, facing whoever arrives.
        wanderRadius: 0,
        interactive: true,
        // The legendary frost set at +10: what the path that starts here leads to.
        outfit: {
            helmet: 'frost-helm-legendary',
            chest: 'frost-chest-legendary',
            weapon: 'frost-sword-legendary',
            offhand: 'frost-shield-legendary',
            gloves: 'frost-gloves-legendary',
            boots: 'frost-boots-legendary',
            level: 10,
            look: { hair: 'ash', eyes: 'blue' },
            mount: 'pegasus',
            mountName: 'Frimas',
        },
    },
    alchemist: {
        id: 'alchemist',
        name: 'Alchimiste',
        bodyKey: 'body.alchemist',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps his house: the biome's leavings in, potions out (ADR 0026).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'flask',
    },
    // A signpost, not a person: the hall's facade is clickable, but nothing on
    // it says so. Named in green like a keeper, it tells the way in (ADR 0035).
    'auction-sign': {
        id: 'auction-sign',
        name: 'Hôtel des ventes',
        bodyKey: 'body.auction-sign',
        tilesPerSecond: 1,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'scales',
    },
    // The account's chest (ADR 0079), by the auction house's door: a chest,
    // not a person, clicked like a keeper; every character of the account
    // finds the same things in it, in every village.
    'stash-chest': {
        id: 'stash-chest',
        name: 'Coffre',
        bodyKey: 'body.stash-chest',
        tilesPerSecond: 1,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
        interactive: true,
    },
    // A village's wanted board (ADR 0096), on its square: clicked like a
    // keeper, it shows the day's bounties.
    'wanted-board': {
        id: 'wanted-board',
        name: 'Avis de recherche',
        bodyKey: 'body.wanted-board',
        tilesPerSecond: 1,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        wanderRadius: 0,
        interactive: true,
    },
    captain: {
        id: 'captain',
        name: 'Capitaine Aldric',
        bodyKey: 'body.captain',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Holds the capital's square: the meadow's quests start and end with him (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'quest',
    },
    hermit: {
        id: 'hermit',
        name: 'Ermite Aubépin',
        bodyKey: 'body.hermit',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps the Circle of the Elders, among its standing stones (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'quest',
    },
    lookout: {
        id: 'lookout',
        name: 'Guetteuse Maëlle',
        bodyKey: 'body.lookout',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Watches the country from the foot of her tower (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'quest',
    },
    ranger: {
        id: 'ranger',
        name: 'Garde-forestière Ysolde',
        bodyKey: 'body.ranger',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Waits in Sylvebois for whoever comes from the meadow (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'quest',
    },
    stablemaster: {
        id: 'stablemaster',
        name: 'Palefrenier Gaspard',
        bodyKey: 'body.stablemaster',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps the capital's stable, out on the road (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'horseshoe',
    },
    horse: {
        id: 'horse',
        name: 'Cheval',
        bodyKey: 'body.meadow-horse',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 4,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        nameplate: false,
        // Grazes in the capital's paddock (ADR 0047): a tile from home, the fence round it.
        wanderRadius: 1,
        restMs: 7000,
    },
    'camel-driver': {
        id: 'camel-driver',
        name: 'Chamelier Idris',
        bodyKey: 'body.camel-driver',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 12,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        // Keeps the camel drivers' relay, out on the road from Sablerouge (ADR 0047).
        wanderRadius: 0,
        interactive: true,
        mapIcon: 'horseshoe',
    },
    camel: {
        id: 'camel',
        name: 'Chameau',
        bodyKey: 'body.desert-camel',
        tilesPerSecond: 2,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 6,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        nameplate: false,
        // Loose about the relay (ADR 0047): no fence, they wander its round.
        wanderRadius: 3,
        restMs: 9000,
    },
    deer: {
        id: 'deer',
        name: 'Cerf',
        bodyKey: 'body.deer',
        tilesPerSecond: 3,
        health: 1,
        damage: 0,
        attackIntervalMs: 10_000,
        aggroRange: 1,
        leashRange: 14,
        respawnMs: 10_000,
        xp: 0,
        peaceful: true,
        nameplate: false,
        // A herd grazes wide.
        wanderRadius: 7,
    },
};
/**
 * How much stronger an elite is than the creature it is kin to, and how long
 * it keeps away once killed. Tuning data, not a formula of the fight: the
 * fight reads the resulting health and damage like any other mob's.
 *
 * - `level`: one to three above the top of the biome's band, so the plate
 *   warns a player of the biome's own level before the first blow;
 * - `health`, `damage`, `xp`: multipliers of the kin's own;
 * - `respawnMs`: a rare comes back in about ten minutes, a legendary in two
 *   hours (and the server staggers them, `MobController`).
 */
export const ELITE_SCALING = Object.freeze({
    // Hardened on 2026-09-28 (a rare fell in a few blows): a real fight at the
    // band's top. A legendary keeps its health, its boss must outlast it (test).
    rare: { levelOverBand: 1, health: 4, damage: 1.6, xp: 5, respawnMs: 10 * 60_000 },
    epic: { levelOverBand: 2, health: 6.5, damage: 1.9, xp: 12, respawnMs: 40 * 60_000 },
    legendary: { levelOverBand: 3, health: 10, damage: 2.2, xp: 25, respawnMs: 120 * 60_000 },
});
/**
 * Highest level of each biome's band. Written here rather than read from
 * `biomes.ts`, which imports this table; a test holds the two together.
 */
export const ELITE_BAND_TOP = Object.freeze({
    meadow: 4,
    forest: 7,
    swamp: 11,
    desert: 15,
    snow: 20,
});
/**
 * A region whose chain pays part of the experience pays less for its
 * creatures (ADR 0047): `QUEST_XP` of `quests/<biome>.ts` replaces the
 * bestiary's figure. Applied before the elites are drawn from their kin, so
 * they follow.
 */
function withQuestXp(table) {
    const copy = { ...table };
    for (const [id, entry] of Object.entries(copy)) {
        const xp = QUEST_XP[id];
        if (xp !== undefined)
            copy[id] = { ...entry, xp };
    }
    return copy;
}
for (const [id, entry] of Object.entries(withQuestXp(DEFINITIONS))) {
    DEFINITIONS[id] = entry;
}
function elite(id, name, kin, rarity, band) {
    const scale = ELITE_SCALING[rarity];
    return {
        ...kin,
        id,
        name,
        rarity,
        level: ELITE_BAND_TOP[band] + scale.levelOverBand,
        health: Math.round(kin.health * scale.health),
        damage: Math.round(kin.damage * scale.damage),
        xp: Math.round(kin.xp * scale.xp),
        respawnMs: scale.respawnMs,
        // It notices a player a step sooner and roams its whole clearing.
        aggroRange: kin.aggroRange + 1,
        // Its calm kin's elite is not calm: meeting one stays an event.
        passive: false,
        leashRange: kin.leashRange + 2,
        wanderRadius: 2,
    };
}
const ELITES = {
    'plague-rat': elite('plague-rat', 'Rat pesteux', DEFINITIONS.rat, 'rare', 'meadow'),
    'scarred-boar': elite('scarred-boar', 'Sanglier balafré', DEFINITIONS.boar, 'epic', 'meadow'),
    'iron-snout': elite('iron-snout', 'Groin-de-Fer', DEFINITIONS.boar, 'legendary', 'meadow'),
    'ash-wolf': elite('ash-wolf', 'Loup cendré', DEFINITIONS.wolf, 'rare', 'forest'),
    'goblin-chief': elite('goblin-chief', 'Chef gobelin', DEFINITIONS.goblin, 'epic', 'forest'),
    'silver-fang': elite('silver-fang', 'Croc-d’Argent', DEFINITIONS.wolf, 'legendary', 'forest'),
    'venom-toad': elite('venom-toad', 'Crapaud venimeux', DEFINITIONS.toad, 'rare', 'swamp'),
    'lizard-shaman': elite('lizard-shaman', 'Chaman lézard', DEFINITIONS.lizardman, 'epic', 'swamp'),
    'bog-king': elite('bog-king', 'Roi des Tourbières', DEFINITIONS.toad, 'legendary', 'swamp'),
    'mangy-hyena': elite('mangy-hyena', 'Hyène galeuse', DEFINITIONS.hyena, 'rare', 'desert'),
    'cursed-mummy': elite('cursed-mummy', 'Momie maudite', DEFINITIONS.mummy, 'epic', 'desert'),
    'forgotten-pharaoh': elite('forgotten-pharaoh', 'Pharaon oublié', DEFINITIONS.mummy, 'legendary', 'desert'),
    'ice-bear': elite('ice-bear', 'Ours des glaces', DEFINITIONS['polar-bear'], 'rare', 'snow'),
    'howling-yeti': elite('howling-yeti', 'Yéti hurleur', DEFINITIONS.yeti, 'epic', 'snow'),
    'elder-blizzard': elite('elder-blizzard', 'Vieux Blizzard', DEFINITIONS.yeti, 'legendary', 'snow'),
};
/**
 * The creatures a dragon's mark changed (ADR 0087): their kin's stat block,
 * experience and table (the Saignée's treants still shed the bark the
 * druid asks for next), under a body and a name of their own. Maddened, so
 * never passive.
 */
const QUARRY_MOBS = Object.fromEntries(QUARRY_MOB_IDS.map((id) => {
    const { kin, name } = QUARRY_KIN[id];
    return [id, { ...DEFINITIONS[kin], id, name, bodyKey: `body.${id}`, passive: false }];
}));
/**
 * How much stronger a wandering rare is than its kin (ADR 0094): between an
 * epic elite and a legendary one, two levels over its band, because it is
 * met by chance, often by a player alone, and must stay a fight one wins.
 * It never comes back by itself: the server raises it (`RareHunt`), so its
 * `respawnMs` is never read.
 */
export const RARE_SCALING = Object.freeze({
    levelOverBand: 2,
    health: 7,
    damage: 1.8,
    xp: 15,
});
const RARES = Object.fromEntries(RARE_MOB_IDS.map((id) => {
    const { kin: kinId, biome, name } = RARE_MOBS[id];
    const kin = DEFINITIONS[kinId];
    return [
        id,
        {
            ...kin,
            id,
            name,
            rarity: 'legendary',
            level: ELITE_BAND_TOP[biome] + RARE_SCALING.levelOverBand,
            health: Math.round(kin.health * RARE_SCALING.health),
            damage: Math.round(kin.damage * RARE_SCALING.damage),
            xp: Math.round(kin.xp * RARE_SCALING.xp),
            respawnMs: 60_000,
            aggroRange: kin.aggroRange + 1,
            passive: false,
            leashRange: kin.leashRange + 4,
            wanderRadius: 3,
        },
    ];
}));
/**
 * How much stronger a bounty's target is than its kin (ADR 0096): well under
 * a wandering rare (health ×7), a fight a player of the region wins alone. The
 * top of its band, so the whole region may take it on. The server raises it
 * for whoever took the bounty (`BountyDesk`), so its `respawnMs` is never read.
 */
export const BOUNTY_SCALING = Object.freeze({
    health: 3,
    damage: 1.4,
    xp: 4,
});
const BOUNTY_TARGETS = Object.fromEntries(BOUNTY_MOB_IDS.map((id) => {
    const { kin: kinId, biome, name } = BOUNTIES[id];
    const kin = DEFINITIONS[kinId];
    return [
        id,
        {
            ...kin,
            id,
            name,
            rarity: 'rare',
            level: ELITE_BAND_TOP[biome],
            health: Math.round(kin.health * BOUNTY_SCALING.health),
            damage: Math.round(kin.damage * BOUNTY_SCALING.damage),
            xp: Math.round(kin.xp * BOUNTY_SCALING.xp),
            respawnMs: 60_000,
            passive: false,
            leashRange: kin.leashRange + 4,
            wanderRadius: 2,
        },
    ];
}));
/**
 * The creatures of the night (ADR 0093): their sleeper's stat block,
 * experience and table under a body and a name of their own, so a
 * den keeps its pace whoever holds it. Never calm: the night hunts.
 */
const NIGHT_DEFINITIONS = Object.fromEntries(NIGHT_MOB_IDS.map((id) => {
    const { sleeper, bodyKey, name } = NIGHT_MOBS[id];
    const kin = DEFINITIONS[sleeper];
    const night = {
        ...kin,
        id,
        name,
        bodyKey,
        passive: false,
        behaviours: [],
    };
    return [id, night];
}));
/**
 * The creature of each biome whose measure an invasion takes (ADR 0050): a
 * raider fights like it, under a goblin's body and name, so the raid is at
 * the level of the village it falls on.
 */
const INVADER_KIN = Object.freeze({
    meadow: 'boar',
    forest: 'goblin',
    swamp: 'lizardman',
    desert: 'mummy',
    snow: 'polar-bear',
});
/**
 * A raider: its biome's creature in goblin's clothes. It is summoned, so it
 * never comes back and leaves nothing; a long leash, because it marches on
 * the square from the outskirts and fights whoever stands in the way.
 */
function raider(biome) {
    const kin = DEFINITIONS[INVADER_KIN[biome]];
    return {
        ...DEFINITIONS.goblin,
        id: invaderOf(biome),
        name: 'Pillard gobelin',
        health: kin.health,
        damage: kin.damage,
        defence: kin.defence ?? 0,
        level: kin.level,
        xp: kin.xp,
        aggroRange: 6,
        leashRange: 40,
        wanderRadius: 4,
        loot: [],
    };
}
/**
 * The warlord of the last wave: an epic among raiders, a level over the
 * biome's band. Its health is only the base: the director raises it with
 * the number of defenders (`warlordHealth`).
 */
function warlord(biome) {
    const kin = DEFINITIONS[INVADER_KIN[biome]];
    return {
        ...raider(biome),
        id: warlordOf(biome),
        name: 'Chef de guerre gobelin',
        rarity: 'epic',
        level: ELITE_BAND_TOP[biome] + 1,
        health: kin.health * 8,
        damage: Math.round(kin.damage * 1.7),
        xp: kin.xp * 10,
        aggroRange: 7,
        wanderRadius: 2,
    };
}
/** The legendary elite of each biome, whose body and measure its giant takes. */
const GIANT_KIN = Object.freeze({
    meadow: 'iron-snout',
    forest: 'silver-fang',
    swamp: 'bog-king',
    desert: 'forgotten-pharaoh',
    snow: 'elder-blizzard',
});
/**
 * The giant of a world event: the biome's legendary elite, a level over it,
 * in a body of its own drawn far larger on a wide canvas (`body.giant-<biome>`), its health only the base the director raises with
 * the number of fighters about (`giantHealth`). Slower than its kin, as
 * heavy things are, and summoned: no table, no return.
 */
function giant(biome) {
    const kin = ELITES[GIANT_KIN[biome]];
    return {
        ...kin,
        id: giantOf(biome),
        name: `${kin.name} géant`,
        bodyKey: `body.giant-${biome}`,
        rarity: 'legendary',
        level: (kin.level ?? 1) + 1,
        health: kin.health * 3,
        damage: Math.round(kin.damage * 1.4),
        xp: kin.xp * 2,
        tilesPerSecond: Math.max(1, kin.tilesPerSecond * 0.75),
        aggroRange: 7,
        leashRange: 50,
        wanderRadius: 0,
        loot: [],
        scale: 2,
        cleave: 2,
    };
}
/**
 * The gold cart of a convoy (ADR 0050): peaceful, so no player strikes it and
 * no creature hunts it; the director walks it along the road and holds it up
 * while raiders stand near or nobody escorts it.
 */
const GOLD_CART = {
    id: GOLD_CART_ID,
    name: 'Chariot d’or',
    bodyKey: 'body.gold-cart',
    // A heavy load at a trot: the seventy tiles of road last a minute and a half.
    tilesPerSecond: 0.8,
    health: 1,
    damage: 0,
    attackIntervalMs: 10_000,
    aggroRange: 1,
    leashRange: 400,
    respawnMs: 10_000,
    xp: 0,
    peaceful: true,
    wanderRadius: 0,
};
const EVENT_MOBS = Object.fromEntries([
    ...EVENT_BIOMES.flatMap((biome) => [
        [invaderOf(biome), raider(biome)],
        [warlordOf(biome), warlord(biome)],
        [giantOf(biome), giant(biome)],
    ]),
    [GOLD_CART_ID, GOLD_CART],
]);
const REGIONAL_TOWNSFOLK = {
    ...DEFINITIONS,
    ...ELITES,
    ...withQuestXp(BOSS_DEFINITIONS),
    ...EVENT_MOBS,
    ...QUARRY_MOBS,
    ...RARES,
    ...NIGHT_DEFINITIONS,
    ...BOUNTY_TARGETS,
    ...SHEFIRA_DEFINITIONS,
    // Validated like any other by `MobDefinitionSchema` just below.
    ...QUEST_NPCS,
};
/**
 * A recruit at the Company's quarter (ADR 0067): the same townsperson, body
 * and name, standing still by the war table, under an id of its own so the
 * quests of its region are neither given nor taken back there, and without
 * the quests' scroll on the maps.
 */
const COMPANY_RECRUIT_DEFINITIONS = Object.fromEntries(Object.values(COMPANY_RECRUITS).map((mob) => {
    const { mapIcon: _scroll, ...base } = REGIONAL_TOWNSFOLK[mob];
    return [companyMobOf(mob), { ...base, id: companyMobOf(mob), wanderRadius: 0 }];
}));
/**
 * The Company in Shefira's arena (ADR 0068 §4): the same townsfolk, body and
 * name, walking where the director sends them. Scripted allies, not targets:
 * peaceful, so neither a player nor a creature ever fights them, and silent
 * to a click; their health is the director's to take, paced to their fall.
 */
const ARENA_ALLY_DEFINITIONS = Object.fromEntries(ARENA_ALLY_IDS.map((id) => {
    const source = ARENA_ALLY_SOURCES[id];
    const { mapIcon: _scroll, interactive: _click, ...base } = REGIONAL_TOWNSFOLK[source];
    return [id, { ...base, id, peaceful: true, wanderRadius: 0, health: ARENA_ALLY_HEALTH }];
}));
/** Parsed once at module load: a malformed table fails the server at boot, not mid-game. */
const ALL_DEFINITIONS = {
    ...REGIONAL_TOWNSFOLK,
    ...COMPANY_RECRUIT_DEFINITIONS,
    ...ARENA_ALLY_DEFINITIONS,
};
export const MOBS = Object.freeze(Object.fromEntries(MOB_IDS.map((id) => [id, MobDefinitionSchema.parse(ALL_DEFINITIONS[id])])));
/** Whether a mob id names a townsperson who answers a click (`interactive`). */
export function isInteractiveMob(mobId) {
    const parsed = MobIdSchema.safeParse(mobId);
    return parsed.success && MOBS[parsed.data].interactive;
}
export function getMob(id) {
    return MOBS[id];
}
//# sourceMappingURL=mobs.js.map